import { type UseMutateFunction } from '@tanstack/react-query'
import type { TFunction } from 'shared/translations'
import type {
  FormData,
  Account,
  SeverityTypes,
  DateRange,
  GroupedRecommendationItem,
  RecommendationTypes,
  StatusTypes,
  MappedEventsData,
  SeverityTypesNumber,
  GroupRecommendationByTypes,
  SeverityMetaData,
  RecommendationParamsValues,
  RecommendationStatus,
  UpdateStatusOptions,
  RecommendationRuleConfigRequestChanges
} from 'src/recommendations/types'
import { type AnalyticsReq, type AnalyticsReqOrderBy, type FeedResp, type Filter } from 'shared/api/analytics'
import { type Client } from 'shared/context/ClientContext'
import { type ClientAccountsResp } from 'shared/api/accounts'
import { getPlatformDetailsByPlatform } from 'src/dataSource/utils'
import {
  getRecommendationTypes,
  getSeverity,
  HEALTH_CHECKERS_V2_SOURCE_SERVICE,
  recommendationItemStatusNone
} from 'src/recommendations/constants'
import { logAndReportError } from 'shared/errorReporter'
import { convertSnakeCaseToCamelCase } from 'shared/utils/stringFormatter'
import { type ClientError } from 'shared/api/fetch'
import { addDays, formatDateToString, format, parseISO } from 'shared/dateFns'
import {
  type RecommendationsRulesMetadata,
  type RecommendationRuleConfigResponse,
  type RecommendationRuleStatusResponse,
  type RecommendationsRulesStatus,
  type RecommendationRuleStatusRequest,
  RecommendationParamTypeFormToAPI
} from 'shared/api/recommendations'
import { type RecommendationMetadata } from 'shared/assetsBucket/recommendations'
import { type ActiveFilterTypes } from 'src/recommendations/components/Toolbar'
import { type FieldPath } from 'react-hook-form'
import { objectEntries } from 'shared/utils'
import { type ChipVariant } from '@precis-digital/kurama/Chip'
import { type Theme } from '@precis-digital/kurama/theme'

interface GroupRecommendationItemsProps {
  clientId: number
  events: MappedEventsData[]
  recommendationsMetadata: RecommendationsRulesMetadata
  groupBy: GroupRecommendationByTypes
  recommendationsRulesStatuses: RecommendationRuleStatusResponse[]
  recommendationsRulesConfigs: RecommendationRuleConfigResponse[]
  hasAccessToTestingRule: boolean
  shouldShowInternalFeatures: boolean
  theme: Theme
}

interface GroupRecommendationItemsReturn {
  data: GroupedRecommendationItem[]
  inactiveData: GroupedRecommendationItem[]
  date: string
  lastUpdatedTimestamp: string | null
}

/**
 * Groups recommendation items by their rule ID or rule ID and date and combines related account information
 * @param {GroupRecommendationItemsProps} props - Object containing events, recommendations, and dataSources
 * @returns {GroupRecommendationItemsReturn} Object containing grouped data and formatted date
 */
export const groupRecommendationItems = ({
  clientId,
  events,
  recommendationsMetadata,
  groupBy,
  recommendationsRulesStatuses,
  recommendationsRulesConfigs,
  hasAccessToTestingRule,
  shouldShowInternalFeatures,
  theme
}: GroupRecommendationItemsProps): GroupRecommendationItemsReturn => {
  let latestTimestamp: Date | null = null
  const groupedMap = new Map<string, GroupedRecommendationItem>()
  const inactiveMap = new Map<string, GroupedRecommendationItem>()

  for (const event of events) {
    const ruleId = event.ruleId
    const recommendationRuleStatus = recommendationsRulesStatuses.find((status) => status.ruleId === ruleId)
    const recommendationRuleConfig = recommendationsRulesConfigs.find((rule) => rule.ruleId === ruleId)
    const recommendationMetadata = recommendationsMetadata[ruleId]

    latestTimestamp = updateLatestTimestamp(latestTimestamp, event.timestamp)

    if (
      !isRecommendationEventValid(event, recommendationMetadata, hasAccessToTestingRule, shouldShowInternalFeatures)
    ) {
      continue
    }

    let key = ruleId
    if (groupBy === 'ruleIdAndDate') {
      const dateStr = formatDateToString({
        date: event.timestamp,
        targetFormat: 'MMM d, yyyy'
      })
      key = `${ruleId}-${dateStr}`
    }
    const existingItem = inactiveMap.get(ruleId) ?? groupedMap.get(key)
    if (existingItem != null) {
      updateExistingRecommendation(existingItem, event)
    } else {
      const groupedItem = createNewRecommendationItem(recommendationMetadata, {
        clientId,
        key,
        event,
        recommendationRuleStatus,
        recommendationRuleConfig,
        theme
      })
      if (groupedItem.status === 'ignored' || groupedItem.status === 'ignoredUntil') {
        inactiveMap.set(ruleId, groupedItem)
      } else {
        groupedMap.set(key, groupedItem)
      }
    }
  }

  const notInGroupedData: (rule: RecommendationMetadata) => boolean = ({ id }) => {
    return !Array.from(groupedMap.entries()).some(([_, rule]) => rule.ruleId === id)
  }

  Object.values(recommendationsMetadata)
    .filter(({ id }) => !inactiveMap.has(id))
    .filter(notInGroupedData)
    .filter((rule) => !shouldHideRule(rule, hasAccessToTestingRule, shouldShowInternalFeatures))
    .forEach((rule) => {
      const ruleId = rule.id
      const recommendationRuleStatus = recommendationsRulesStatuses.find((status) => status.ruleId === ruleId)
      const recommendationRuleConfig = recommendationsRulesConfigs.find((rule) => rule.ruleId === ruleId)
      inactiveMap.set(
        rule.id,
        createNewRecommendationItem(rule, {
          clientId,
          recommendationRuleStatus,
          recommendationRuleConfig,
          theme
        })
      )
    })

  const formattedDate = formatDateToString({ date: latestTimestamp ?? '', targetFormat: 'MMM d, yyyy' })
  const lastUpdatedTimestamp = latestTimestamp == null ? null : latestTimestamp.toISOString()

  return {
    data: Array.from(groupedMap.values()),
    inactiveData: Array.from(inactiveMap.values()),
    date: formattedDate,
    lastUpdatedTimestamp
  }
}

const LEGACY_LINKEDIN_RULE_PREFIX = 'linkedin'
const LINKEDIN_ADS_RULE_PREFIX = 'linkedin_ads'

/**
 * Notifications already sent for the legacy Health Checker rules use `linkedin…` rule ids.
 * Each of those rules has an identical `linkedin_ads…` twin, and that twin is the one still shown.
 */
export const mapLegacyLinkedinRuleId = (ruleId: string): string => {
  if (ruleId.startsWith(LINKEDIN_ADS_RULE_PREFIX)) {
    return ruleId
  }
  if (ruleId.startsWith(LEGACY_LINKEDIN_RULE_PREFIX)) {
    return `${LINKEDIN_ADS_RULE_PREFIX}${ruleId.slice(LEGACY_LINKEDIN_RULE_PREFIX.length)}`
  }
  return ruleId
}

/**
 * Updates the latest timestamp by comparing with a new timestamp
 * @param {Date | null} latestTimestamp - Current latest timestamp
 * @param {string} timestamp - New timestamp to compare
 * @returns {Date | null} Updated latest timestamp
 */
const updateLatestTimestamp = (latestTimestamp: Date | null, timestamp: string): Date | null => {
  const recommendationDate = new Date(timestamp)
  if (latestTimestamp === null || recommendationDate > latestTimestamp) {
    return recommendationDate
  }
  return latestTimestamp
}

/**
 * Validates if a recommendation item exists and has valid data
 * @param {FeedResp} event - The recommendation item to validate
 * @param {RecommendationMetadata} recommendationMetadata - Recommendation metadata
 * @param {boolean} hasAccessToTestingRule - Whether the user has access to testing rules
 * @returns {boolean} Whether the recommendation item is valid
 */
export const isRecommendationEventValid = (
  event: MappedEventsData,
  recommendationMetadata: RecommendationMetadata | undefined,
  hasAccessToTestingRule: boolean,
  shouldShowInternalFeatures: boolean
): boolean => {
  if (event.data == null) {
    logAndReportError(new Error('Invalid recommendation item: jsonPayload is null'))
    return false
  }

  if (recommendationMetadata == null) {
    if (event.stage === 'active') {
      logAndReportError(new Error(`Invalid recommendation item: rule ID ${event.ruleId} not found in recommendations`))
    }
    return false
  }

  if (shouldHideRule(recommendationMetadata, hasAccessToTestingRule, shouldShowInternalFeatures)) {
    return false
  }

  if (event.stage === 'testing' && event.stage !== recommendationMetadata.stage) {
    return false
  }

  return true
}

/**
 * Checks if a rule should be hidden
 * @param {RecommendationMetadata} recommendationMetadata - Recommendation metadata
 * @param {boolean} hasAccessToTestingRule - Whether the user has access to testing rules
 * @returns {boolean} Whether the rule should be hidden
 */
export const shouldHideRule = (
  recommendationMetadata: RecommendationMetadata,
  hasAccessToTestingRule: boolean,
  shouldShowInternalFeatures: boolean
): boolean => {
  if (recommendationMetadata.stage === 'deprecated') {
    return true
  }

  if (recommendationMetadata.stage === 'testing' && !hasAccessToTestingRule) {
    return true
  }

  if (getPlatformDetailsByPlatform(recommendationMetadata.platform)?.isInternalOnly && !shouldShowInternalFeatures) {
    return true
  }

  return false
}

/**
 * Options for creating a new grouped recommendation item
 * @param {number} clientId - Client ID
 * @param {string} key - Unique identifier for the recommendation
 * @param {MappedEventsData} event - Recommendation event data
 * @param {RecommendationRuleStatusResponse} recommendationRuleStatus - Recommendation rule status
 * @param {RecommendationRuleConfigResponse} recommendationRuleConfig - Recommendation rule config
 */
interface CreateNewRecommendationItemOptions {
  clientId: number
  key?: string
  event?: MappedEventsData
  recommendationRuleStatus?: RecommendationRuleStatusResponse
  recommendationRuleConfig?: RecommendationRuleConfigResponse
  theme: Theme
}

/**
 * Creates a new grouped recommendation item from raw data
 * @param {RecommendationMetadata} recommendation - Recommendation metadata
 * @param {CreateNewRecommendationItemOptions} options - Options for creating a new recommendation item
 * @returns {GroupedRecommendationItem} Formatted recommendation item
 */
export const createNewRecommendationItem: (
  recommendation: RecommendationMetadata,
  options: CreateNewRecommendationItemOptions
) => GroupedRecommendationItem = (recommendation, options): GroupedRecommendationItem => {
  const ruleId = recommendation.id

  const { clientId, key, event, recommendationRuleStatus, recommendationRuleConfig, theme } = {
    key: ruleId,
    ...options
  }

  const recommendationTypes = getRecommendationTypes(theme)

  const groupedRecommendationItemStatus = mapAPIStatusToStatus(recommendationRuleStatus)

  const paramsValues = objectEntries(recommendation.params ?? {}).reduce<GroupedRecommendationItem['paramsValues']>(
    (acc, [key, { default: defaultValue }]) => {
      acc[key] = recommendationRuleConfig?.params?.[key] ?? defaultValue
      return acc
    },
    {}
  )

  const newRecommendationItem: GroupedRecommendationItem = {
    clientId,
    key:
      groupedRecommendationItemStatus.status === 'ignored' || groupedRecommendationItemStatus.status === 'ignoredUntil'
        ? ruleId
        : key,
    name: recommendationRuleConfig?.name ?? recommendation.name,
    originalName: recommendation.name,
    messageId: event?.messageId ?? ruleId,
    category: recommendation.category,
    Icon: recommendationTypes[recommendation.category].icon,
    backgroundColor: recommendationTypes[recommendation.category].backgroundColor,
    severity: recommendationRuleConfig?.severity ?? recommendation.severity,
    originalSeverity: recommendation.severity,
    platform: recommendation.platform,
    recommendation: recommendationRuleConfig?.recommendation ?? recommendation.recommendation,
    originalRecommendation: recommendation.recommendation,
    timestamp: event?.timestamp ?? new Date().toISOString(),
    sourceService: event?.sourceService ?? HEALTH_CHECKERS_V2_SOURCE_SERVICE,
    ruleId: recommendation.id,
    stage: recommendation.stage,
    accounts: [] as Account[],
    defaultColumns: recommendation.defaultColumns,
    outputColumns: recommendation.outputColumns,
    params: recommendation.params ?? {},
    paramsValues,
    configId: recommendationRuleConfig?.id,
    platformEntityType: recommendation.platformEntityType,
    totalNumEntities: 0,
    totalNumEntitiesWithIssues: 0,
    events: [],
    ...groupedRecommendationItemStatus
  }

  if (event != null) {
    const account = getAccount(event.account)
    if (account != null) {
      newRecommendationItem.accounts.push(account)
      newRecommendationItem.totalNumEntities += event.totalNumEntities
      newRecommendationItem.totalNumEntitiesWithIssues += event.totalNumEntitiesWithIssues
      newRecommendationItem.events.push(event)
    }
  }

  return newRecommendationItem
}

/**
 * Updates an existing recommendation item with new account information
 * @param {GroupedRecommendationItem} existingRecommendationItem - The item to update
 * @param {FeedResp} event - New recommendation data
 * @param {ClientAccountsResp[] | undefined} dataSources - Available data sources
 */
const updateExistingRecommendation = (
  existingRecommendationItem: GroupedRecommendationItem,
  event: MappedEventsData
): void => {
  const account = getAccount(event.account)

  if (account != null) {
    existingRecommendationItem.totalNumEntities += event.totalNumEntities
    existingRecommendationItem.totalNumEntitiesWithIssues += event.totalNumEntitiesWithIssues
    existingRecommendationItem.events.push(event)

    const hasAlreadyAddedAccount: boolean = existingRecommendationItem.accounts.some(
      (acc) => acc.id === (account?.id ?? '')
    )

    if (!hasAlreadyAddedAccount) {
      existingRecommendationItem.accounts.push(account)
    }
  }
}

/**
 * Retrieves account information from data sources based on tags
 * @param {string[]} tags - Array of tags containing account information
 * @param {ClientAccountsResp[] | undefined} dataSources - Available data sources
 * @returns {Account | undefined} Account information if found
 */
const getAccount = (dataSource: ClientAccountsResp): Account => {
  const dataSourceMeta = getPlatformDetailsByPlatform(dataSource.platform)
  return {
    id: dataSource.externalAccountId,
    name: dataSource.name ?? dataSource.externalAccountId,
    iconUrl: dataSourceMeta?.iconUrl ?? ''
  }
}

const AccountIdTag = 'account id: '

/**
 * Extracts account ID from tags array
 * @param {string[]} tags - Array of tags
 * @returns {string | undefined} Account ID if found
 */
export const getAccountId = (tags: string[]): string | undefined => {
  return tags
    .find((tag) => {
      return tag.startsWith(AccountIdTag)
    })
    ?.substring(AccountIdTag.length)
    ?.trim()
}

/**
 * Extracts a specific property from tags array
 * @param {string[]} tags - Array of tags
 * @param {string} propertyName - Name of the property to extract
 * @returns {T} Extracted property value
 */
export const getTagProperty = <T extends string>(tags: string[], propertyName: string): T => {
  const propertyTag = `${propertyName}: `
  return tags
    .find((tag) => {
      return tag.startsWith(propertyTag)
    })
    ?.substring(propertyTag.length)
    ?.trim() as T
}

/**
 * Creates a function to fetch feed data with optional filters and ordering
 * @param {UseMutateFunction} post - Mutation function for posting data
 * @param {object} options - Optional filters and ordering parameters
 * @returns {Function} Function that fetches feed data for a given client
 */
export const getFeed =
  (
    post: UseMutateFunction<FeedResp[], ClientError, AnalyticsReq>,
    options?: {
      filters?: Filter[]
      orderBy?: AnalyticsReqOrderBy
    }
  ) =>
  (currentClient: Client): void => {
    const defaultOptions: { filters?: Filter[]; orderBy?: AnalyticsReqOrderBy } = {
      orderBy: { fields: ['timestamp'], direction: 'DESC' },
      filters: [
        {
          field: 'source_service',
          operator: '=',
          value: HEALTH_CHECKERS_V2_SOURCE_SERVICE
        }
      ]
    }
    post({
      collection: 'event_feed',
      report: 'event_feed',
      clientId: currentClient?.id,
      ...defaultOptions,
      ...(options != null && {
        ...(options.filters != null && options.filters.length > 0 && { filters: options.filters }),
        ...(options.orderBy != null && { orderBy: options.orderBy })
      })
    })
  }

/**
 * Groups data by severity level
 * @param {T[]} data - Array of items containing severity property
 * @returns {Record<SeverityTypes, number>} Count of items by severity
 */
export const groupBySeverity = <T extends { timestamp: string; severity: SeverityTypes }>(
  data: T[]
): Record<SeverityTypes, number> & { timestamp: string | undefined } => {
  const result: Record<SeverityTypes, number> & { timestamp: string | undefined } = {
    timestamp: data.length > 0 ? format(new Date(data[0].timestamp), 'MMM d, yyyy') : '',
    critical: 0,
    moderate: 0,
    low: 0
  }

  data.forEach((item) => {
    if (item.severity === 'critical') {
      result.critical++
    } else if (item.severity === 'moderate') {
      result.moderate++
    } else {
      result.low++
    }
  })

  return result
}

interface LineChartData {
  x: string
  low?: number
  moderate?: number
  critical?: number
}

/**
 * Transforms recommendations data into chart-friendly format
 * @param {GroupedRecommendationItem[]} recommendations - Array of recommendations
 * @param {DateRange} selectedDateRange - Selected date range for the chart
 * @returns {LineChartData[]} Formatted data for line chart
 */
export const transformRecommendationsToChartData = (
  recommendations: GroupedRecommendationItem[],
  selectedDateRange: DateRange,
  severityFilters: Record<SeverityTypes, boolean>
): LineChartData[] => {
  const allDates: Record<string, LineChartData> = {}

  const startDate = new Date(selectedDateRange.startDate)
  const endDate = new Date(selectedDateRange.endDate)

  let currentDate = startDate
  while (currentDate.getTime() <= endDate.getTime()) {
    const dateKey = formatDateToString({
      date: currentDate,
      targetFormat: 'yyyy-MM-dd'
    })
    allDates[dateKey] = {
      x: dateKey
    }

    if (severityFilters.critical) {
      allDates[dateKey].critical = 0
    }
    if (severityFilters.moderate) {
      allDates[dateKey].moderate = 0
    }
    if (severityFilters.low) {
      allDates[dateKey].low = 0
    }

    currentDate = addDays(currentDate, 1)
  }

  recommendations.forEach((recommendation) => {
    if (!severityFilters[recommendation.severity]) {
      return
    }
    const date = new Date(recommendation.timestamp)
    const dateKey = formatDateToString({
      date,
      targetFormat: 'yyyy-MM-dd'
    })
    if (allDates[dateKey] != null && recommendation.severity in allDates[dateKey]) {
      ;(allDates[dateKey][recommendation.severity] as number) += 1
    }
  })

  return Object.values(allDates).sort((a, b) => {
    const dateA = new Date(a.x)
    const dateB = new Date(b.x)
    return dateA.getTime() - dateB.getTime()
  })
}

/**
 * `filterData` treats "no option set to true" as "no filter applied" (pass everything) so that
 * `=`/`IN`-style filters only need to flip on the options they select. An `is_null` filter on these
 * dimensions must instead match zero recommendations (severity/type/status/platform are always
 * populated), so it needs to flip `filterData`'s "is any option active" check to true without making
 * any real option pass. This marker key does that: it is never a legitimate dimension value, so
 * direct lookups like `severityFilters[recommendationItem.severity]` never match it.
 */
const BLOCK_ALL_FILTER_MARKER = '__is_null_block_all__'

export const processFilters = <T extends string>(
  cirrusFilters: Filter[],
  fieldName: ActiveFilterTypes,
  defaultValues: Record<T, boolean>
): Record<T, boolean> => {
  const result: Record<string, boolean> = { ...defaultValues }
  const allKeys = Object.keys(defaultValues) as T[]

  const relatedFilters = cirrusFilters.filter((filter) => filter.field === fieldName)
  if (relatedFilters.length === 0) {
    allKeys.forEach((key) => (result[key] = true))
    return result as Record<T, boolean>
  }

  relatedFilters.forEach((filter) => {
    const { operator, value } = filter

    if (operator === '=' && typeof value === 'string') {
      result[value] = true
    } else if (operator === '!=' && typeof value === 'string') {
      const otherKeys = allKeys.filter((key) => key !== value)
      otherKeys.forEach((key) => (result[key] = true))
    } else if (operator === 'IN' && Array.isArray(value)) {
      value.forEach((val) => (result[val as string] = true))
    } else if (operator === 'NOT_IN' && Array.isArray(value)) {
      const includedKeys = allKeys.filter((key) => !value.includes(key))
      includedKeys.forEach((key) => (result[key] = true))
    } else if (operator === 'IS_NOT_NULL') {
      // Field is always populated, so "has a value" matches every option.
      allKeys.forEach((key) => (result[key] = true))
    } else if (operator === 'IS_NULL') {
      // Field is never actually null, so "has no value" matches nothing.
      result[BLOCK_ALL_FILTER_MARKER] = true
    }
  })

  return result as Record<T, boolean>
}

export const convertCirrusFilters = (cirrusFilters: Filter[]): Record<ActiveFilterTypes, Record<string, boolean>> => {
  const severityDefaults: Record<SeverityTypes, boolean> = {
    critical: false,
    moderate: false,
    low: false
  }

  const typeDefaults: Record<RecommendationTypes, boolean> = {
    best_practices: false,
    errors: false,
    performance: false
  }

  const statusDefaults: Record<StatusTypes, boolean> = {
    unresolved: false,
    ongoing: false,
    ignored: false,
    ignoredUntil: false
  }

  const platformDefaults: Record<string, boolean> = {}

  return {
    severity: processFilters<SeverityTypes>(cirrusFilters, 'severity', severityDefaults),
    type: processFilters<RecommendationTypes>(cirrusFilters, 'type', typeDefaults),
    status: processFilters<StatusTypes>(cirrusFilters, 'status', statusDefaults),
    platform: processFilters<string>(cirrusFilters, 'platform', platformDefaults)
  }
}

export const filterData = (
  groupedData: GroupedRecommendationItem[],
  filters: Partial<Record<ActiveFilterTypes, Record<string, boolean>>>
): GroupedRecommendationItem[] => {
  return groupedData.filter((recommendationItem) => {
    const severityFilters = filters.severity ?? {}
    const anySeverityFilterActive = Object.values(severityFilters).some(Boolean)
    const passesSeverityFilter = !anySeverityFilterActive || severityFilters[recommendationItem.severity]

    const typeFilters = filters.type ?? {}
    const anyTypeFilterActive = Object.values(typeFilters).some(Boolean)
    const passesTypeFilter = !anyTypeFilterActive || typeFilters[recommendationItem.category]

    const statusFilters = filters.status ?? {}
    const anyStatusFilterActive = Object.values(statusFilters).some(Boolean)
    const cardStatus = recommendationItem.currentStatus
    const passesStatusFilter = !anyStatusFilterActive || statusFilters[cardStatus]

    const platformFilters = filters.platform ?? {}
    const anyPlatformFilterActive = Object.values(platformFilters).some(Boolean)
    const passesPlatformFilter = !anyPlatformFilterActive || platformFilters[recommendationItem.platform]

    return passesSeverityFilter && passesTypeFilter && passesStatusFilter && passesPlatformFilter
  })
}

export const mapMessageWithParams = (message: string, params: RecommendationParamsValues | undefined): string => {
  if (message === '' || params == null) return message

  const paramPlaceholderRegex = /\{\{params\['([^']+)'\]\}\}/g
  return message.replace(paramPlaceholderRegex, (match, key) => {
    const keyCamelCase = convertSnakeCaseToCamelCase(key)
    if (params[keyCamelCase] == null) return match
    const value = params[keyCamelCase]
    return value.toString()
  })
}

const mapSeverity = (severity: SeverityTypesNumber): SeverityTypes => {
  switch (severity) {
    case '1':
      return 'low'
    case '2':
    case '3':
    case '4':
      return 'moderate'
    case '5':
      return 'critical'
  }
}

/**
 * Type predicate to filter out null values
 */
const isNotNull = <T>(item: T | null): item is T => item !== null

export const mapEventsData = (eventsData: FeedResp[], dataSources: ClientAccountsResp[]): MappedEventsData[] => {
  return eventsData
    .map<MappedEventsData | null>((item) => {
      const account = dataSources.find(
        (dataSource) =>
          dataSource.externalAccountId === getAccountId(item.tags) &&
          dataSource.platform === getPlatformDetailsByPlatform(item.platform)?.platform &&
          dataSource.recommendationsEngineActive
      )
      if (account == null) {
        return null
      }
      return {
        messageId: item.messageId,
        clientId: item.clientId,
        platform: item.platform,
        publishTime: item.publishTime,
        sourceService: item.sourceService,
        account,
        severity: mapSeverity(getTagProperty<SeverityTypesNumber>(item.tags, 'severity')),
        category: getTagProperty<RecommendationTypes>(item.tags, 'category'),
        timestamp: item.timestamp,
        title: item.title,
        ruleId: item.jsonPayload.ruleId,
        stage: item.jsonPayload.stage ?? 'active',
        data: item.jsonPayload.data,
        totalNumEntities: item.jsonPayload.totalNumEntities,
        totalNumEntitiesWithIssues: item.jsonPayload.totalNumEntitiesWithIssues
      }
    })
    .filter(isNotNull)
}

export const formatOutputColumnValue = (value: string | number, type: string): string => {
  if (type === 'string') {
    return value.toString()
  }
  if (type === 'number') {
    const num = typeof value === 'number' ? value : parseFloat(value)
    return Number.isInteger(num) ? num.toString() : num.toFixed(2)
  }
  if (type === 'percentage') {
    const num = (typeof value === 'number' ? value : parseFloat(value)) * 100
    return Number.isInteger(num) ? `${num}%` : `${num.toFixed(2)}%`
  }
  return value.toString()
}

export const transformDataForForm = (data: GroupedRecommendationItem): FormData => {
  const paramsFormValues: FormData['paramsFormValues'] = Object.entries(data.paramsValues).reduce<
    FormData['paramsFormValues']
  >((acc, [key, value]) => {
    acc[key] = value.toString()
    return acc
  }, {})

  return {
    name: data.name,
    recommendation: data.recommendation,
    severity: data.severity,
    paramsFormValues
  }
}

export const transformFormDataToAPI = (
  recommendationItem: GroupedRecommendationItem,
  data: FormData
): RecommendationRuleConfigRequestChanges => {
  const changes: RecommendationRuleConfigRequestChanges = {
    name: data.name,
    recommendation: data.recommendation,
    severity: data.severity
  }

  const changedParams: RecommendationParamsValues = {}
  let hasChangedParams = false

  objectEntries(data.paramsFormValues).forEach(([paramKey, paramStringValue]) => {
    const { type, default: defaultValue } = recommendationItem.params[paramKey]
    const paramValue = RecommendationParamTypeFormToAPI[type](paramStringValue)
    if (paramValue !== defaultValue) {
      changedParams[paramKey] = paramValue
      hasChangedParams = true
    }
  })

  if (hasChangedParams) {
    changes.paramsValues = changedParams
  }

  return changes
}

export const getInputLabels = ({
  fieldName,
  t
}: {
  fieldName: FieldPath<FormData>
  data: FormData
  t: TFunction<'recommendation'>
}): string => {
  const INPUT_LABELS: Partial<Record<FieldPath<FormData>, string>> | undefined = {
    name: t('popup.form.name'),
    recommendation: t('popup.form.message'),
    severity: t('popup.form.severity')
  }
  if (fieldName.includes('params')) {
    const paramKey = fieldName.split('.')[1]
    INPUT_LABELS[fieldName] = paramKey
  }
  return INPUT_LABELS[fieldName] ?? ''
}

/**
 * Maps status to API status
 * @param {StatusTypes} status - The status
 * @returns {Object} The API status
 */
export const mapStatusToAPI = (
  options: UpdateStatusOptions
): Pick<RecommendationRuleStatusRequest, 'ongoing' | 'ignored' | 'ignoredUntil'> => ({
  ongoing: options.status === 'ongoing',
  ignored: options.status === 'ignored',
  ...(options.status === 'ignoredUntil' && { ignoredUntil: formatStatusAPIDate(options.ignoredUntil) })
})

/**
 * Maps API status to status
 * @param {Object} props - Object containing ongoing, ignored, ignoredUntil, and resolvedBy
 * @param {boolean} props.ongoing - Whether the status is ongoing
 * @param {boolean} props.ignored - Whether the status is ignored
 * @param {string | undefined} props.ignoredUntil - The ignored until date
 * @returns {StatusTypes} The status
 */
export const mapAPIStatusToStatus = (
  recommendationRuleStatus: RecommendationsRulesStatus | undefined
): RecommendationStatus => {
  if (recommendationRuleStatus == null) {
    return recommendationItemStatusNone
  }

  const { id: statusId, ongoing, ignored, ignoredUntil, assignee } = recommendationRuleStatus

  const today = formatStatusAPIDate(new Date())
  if (ongoing && !ignored && ignoredUntil == null) {
    return {
      statusId,
      status: 'ongoing',
      currentStatus: 'ongoing',
      ongoing: true,
      ignored: false,
      ignoredUntil: undefined,
      ignoredUntilDate: undefined,
      assignee
    }
  }
  if (!ongoing && ignored && ignoredUntil == null) {
    return {
      statusId,
      status: 'ignored',
      currentStatus: 'ignored',
      ongoing: false,
      ignored: true,
      ignoredUntil: undefined,
      ignoredUntilDate: undefined,
      assignee
    }
  }
  if (!ongoing && !ignored && ignoredUntil != null) {
    if (ignoredUntil > today) {
      return {
        statusId,
        status: 'ignoredUntil',
        currentStatus: 'ignored',
        ongoing: false,
        ignored: false,
        ignoredUntil,
        ignoredUntilDate: parseStatusAPIDate(ignoredUntil),
        assignee
      }
    }
    return { ...recommendationItemStatusNone, statusId }
  }
  if (assignee != null) {
    return { ...recommendationItemStatusNone, statusId, assignee }
  }
  return { ...recommendationItemStatusNone, statusId }
}

export const getSeverityMetaData = (severity: SeverityTypes, theme: Theme): SeverityMetaData => {
  return getSeverity(theme)[severity]
}

export const getSeverityChipVariant = (severity: SeverityTypes): ChipVariant => {
  switch (severity) {
    case 'critical':
      return 'semantic-destructive'
    case 'moderate':
      return 'semantic-warning'
    case 'low':
      return 'semantic-info'
    default:
      return 'tonal'
  }
}

export const formatStatusAPIDate = (date: Date): string => {
  return formatDateToString({ date, targetFormat: 'yyyy-MM-dd' })
}

/**
 * Parses a YYYY-MM-DD status date as a local calendar day instead of UTC midnight.
 *
 * `new Date('2026-09-17')` shifts to the previous local day for users west of UTC.
 */
export const parseStatusAPIDate = (date: string): Date => {
  return parseISO(date)
}
